import React from 'react';
import { Baby, Shield, CheckCircle2, ArrowRight, Phone, GraduationCap, Heart, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { licPlans } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE } from '../components/SEOHead';
import { trackWhatsAppClick } from '../services/analytics';

const ChildInsurancePlans = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const childPlans = licPlans.filter(p => p.category === 'children_future');

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': isTe ? 'ఎల్‌ఐసి పిల్లల భవిష్యత్తు బీమా పాలసీలు | వి. ఎస్. రెడ్డి' : 'LIC Child Insurance Plans | Higher Education & Marriage Planning',
    'description': 'Compare LIC child insurance plans including LIC Amrit Baal (Plan 874), Jeevan Lakshya (Plan 933), and Jeevan Tarun (Plan 934) with advisor V. S. Reddy.',
    'provider': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? "ఎల్‌ఐసి పిల్లల చదువుల పాలసీలు | అమృతబాల్, లక్ష్య & తరుణ్ | VSR Insurance Plus" : "LIC Child Insurance Plans | Higher Education & Marriage Planning | VSR Insurance Plus"}
        description={isTe ? "పిల్లల ఉన్నత విద్య మరియు వివాహం కోసం ప్రభుత్వ హామీ గల ఎల్‌ఐసి పాలసీలు: అమృతబాల్ (₹80 గ్యారెంటీడ్ అడిషన్స్), జీవన్ లక్ష్య మరియు జీవన్ తరుణ్. సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి." : "Secure your child's higher education with sovereign-backed LIC child policies: Amrit Baal, Jeevan Lakshya, and Jeevan Tarun. Compare returns and get custom quotes."}
        canonicalPath="/child-insurance-plans"
        schemaJson={pageSchema}
        breadcrumbs={[{ name: isTe ? 'పిల్లల పాలసీలు' : 'Child Insurance Plans', path: '/child-insurance-plans' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 p-2.5 px-4 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Baby className="w-4 h-4" /> {isTe ? "ప్రభుత్వ హామీతో కూడిన పిల్లల భవిష్యత్తు రక్షణ" : "Sovereign-Backed Child Future Security"}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 tracking-tight">
            {isTe ? "భారతదేశంలో ఎల్‌ఐసి పిల్లల బీమా పథకాలు" : "LIC Child Insurance Plans in India"}
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {isTe
              ? "భారతదేశంలో విద్యా ద్రవ్యోల్బణం ఏటా 10-12% పెరుగుతున్న తరుణంలో, తల్లిదండ్రులకు ఏదైనా అనుకోనిది జరిగినా పిల్లల కలలు 100% నెరవేరేలా ఎల్‌ఐసి పిల్లల పథకాలు ఆర్థిక భరోసా కల్పిస్తాయి."
              : "With higher education inflation topping 10-12% annually in India, dedicated LIC child plans guarantee that your son or daughter's dreams will be 100% funded—even in the unfortunate absence of the parent."}
          </p>
        </div>

        {/* 3 Pillars of Child Protection */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <GraduationCap className="w-10 h-10 text-primary mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "కాలేజీ ప్రవేశ సమయానికి నిధులు" : "College Milestone Timing"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "కాలేజీ అడ్మిషన్ల సమయానికి (18, 21, లేదా 25 ఏళ్ల వయస్సులో) సరిగ్గా సరిపోయేలా సెమిస్టర్ ఫీజుల కోసం నిధులు అందేలా రూపకల్పన."
                : "Plan maturities match exact admission ages (18, 21, or 25 years) so funds are ready when semester fees are due."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Shield className="w-10 h-10 text-emerald-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "ప్రీమియం వేవర్ బెనిఫిట్ (PWB)" : "Premium Waiver Benefit"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "తల్లిదండ్రులు అనుకోకుండా మరణిస్తే మిగిలిన భవిష్యత్తు ప్రీమియంలన్నీ రద్దు చేయబడతాయి, మెచ్యూరిటీ మొత్తం యథావిధిగా పిల్లలకు చేరుతుంది."
                : "If the parent passes away, all remaining future premiums are waived while the maturity corpus remains 100% intact."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Heart className="w-10 h-10 text-rose-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "సార్వభౌమ హామీ భద్రత" : "100% Sovereign Safety"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "ఎల్‌ఐసి చట్టం సెక్షన్ 37 ప్రకారం కేంద్ర ప్రభుత్వ ప్రత్యక్ష గ్యారెంటీతో మీ పిల్లల విద్యా నిధి పూర్తిగా సురక్షితం."
                : "Backed by the sovereign guarantee of the Government of India under Section 37 of the LIC Act 1956."}
            </p>
          </div>
        </div>

        {/* Featured Child Policies */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mb-3">
              {isTe ? "అగ్రశ్రేణి ఎల్‌ఐసి పిల్లల విద్యా పథకాలు" : "Top LIC Child Education Plans"}
            </h2>
            <p className="text-slate-500 text-sm">
              {isTe ? "సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి గారు ధృవీకరించిన అధికారిక నిబంధనలు." : "Explore official policy parameters verified by Senior Advisor V. S. Reddy."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {childPlans.map(plan => (
              <div key={plan.id} className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                <div>
                  <span className="inline-block px-3 py-1 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                    {isTe ? (plan.tagTe || plan.tag) : plan.tag}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {isTe ? (plan.nameTe || plan.name) : plan.name}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                    {isTe ? (plan.shortDescTe || plan.shortDesc) : plan.shortDesc}
                  </p>

                  <ul className="space-y-2.5 mb-6">
                    {(isTe && plan.featuresTe ? plan.featuresTe : plan.features).slice(0, 4).map((f, i) => (
                      <li key={i} className="flex gap-2 items-start text-xs text-slate-600 dark:text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="text-[11px] text-slate-400 block font-medium">
                      {t('monthly_prem')}
                    </span>
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      {isTe ? (plan.premiumTe || plan.premium) : plan.premium}
                    </span>
                  </div>
                  <Link
                    to={`/plan/${plan.id}`}
                    className="px-4 py-2 bg-primary hover:bg-blue-600 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-1.5"
                  >
                    {t('details')} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Guide Callout */}
        <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-3xl p-8 md:p-12 text-white shadow-xl flex flex-col md:flex-row justify-between items-center gap-8 mb-16">
          <div className="max-w-xl">
            <span className="text-xs font-bold text-yellow-300 uppercase tracking-widest block mb-2">
              {isTe ? "సమగ్ర విశ్లేషణ" : "Detailed Analysis"}
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">
              {isTe ? "పిల్లల కోసం సరైన ఎల్‌ఐసి పాలసీని ఎలా ఎంచుకోవాలి?" : "How to Compare LIC Child Insurance Plans"}
            </h2>
            <p className="text-blue-100 text-sm leading-relaxed mb-4">
              {isTe
                ? "ఎల్‌ఐసి అమృతబాల్ (గ్యారెంటీడ్ అడిషన్స్), జీవన్ లక్ష్య (తల్లిదండ్రుల మరణానంతరం ఏటా 10% ఆదాయం) మరియు జీవన్ తరుణ్ (కాలేజీ సెమిస్టర్ల మనీ బ్యాక్) మధ్య పూర్తి పోలిక."
                : "Read our full comparative analysis between LIC Amrit Baal (guaranteed additions), Jeevan Lakshya (parent waiver & family income), and Jeevan Tarun (staged milestone cashflows)."}
            </p>
            <Link
              to="/blog/how-to-compare-lic-child-insurance-plans"
              className="inline-flex items-center gap-2 text-yellow-300 font-bold hover:underline text-sm"
            >
              {isTe ? "తులనాత్మక గైడ్ చదవండి" : "Read Comparison Guide"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto">
            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(isTe ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను పిల్లల చదువుల పాలసీ కొటేషన్ కోసం సంప్రదిస్తున్నాను.' : 'Hi V. S. Reddy, I want to compare LIC child plans for my child. Please help me with a calculation.')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('child_hub_whatsapp')}
              className="px-8 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" /> {isTe ? "వాట్సాప్ కొటేషన్ పొందండి" : "WhatsApp Advisor for Quote"}
            </a>
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium text-center border border-white/20 transition-colors text-sm"
            >
              {isTe ? "ఉచిత కన్సల్టేషన్ బుక్ చేయండి" : "Book Free Consultation"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildInsurancePlans;
